import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const PREFS_KEY = 'dsi_admin_preferences_v1';

const DEFAULT_PREFS = {
  theme: 'normal',
  language: 'fr',
  timezone: 'UTC',
  dateFormat: 'DD/MM/YYYY',
  currency: 'FCFA',
  density: 'standard',
  compactTables: false,
  adminAlerts: true,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function resolveTheme(themePref) {
  if (themePref === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  return themePref;
}

const PreferencesContext = createContext({
  prefs: DEFAULT_PREFS,
  setPrefs: () => {},
  updatePref: () => {},
  resolvedTheme: 'normal',
  language: 'fr',
});

export function PreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(loadPrefs().theme));

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
  }, [prefs]);

  useEffect(() => {
    const apply = () => {
      const resolved = resolveTheme(prefs.theme);
      setResolvedTheme(resolved);
      const root = document.documentElement;
      root.setAttribute('data-theme', resolved);
      root.setAttribute('data-density', prefs.density);
      root.lang = prefs.language === 'en' ? 'en' : 'fr';
      if (prefs.compactTables) root.setAttribute('data-compact-tables', 'true');
      else root.removeAttribute('data-compact-tables');
    };
    apply();

    if (prefs.theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [prefs.theme, prefs.density, prefs.language, prefs.compactTables]);

  const updatePref = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  };

  const value = useMemo(
    () => ({
      prefs,
      setPrefs,
      updatePref,
      resolvedTheme,
      language: prefs.language || 'fr',
    }),
    [prefs, resolvedTheme]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  return useContext(PreferencesContext);
}

export { PREFS_KEY, DEFAULT_PREFS };
